import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../_auth/auth.service';
import { SnackbarService } from '../_services/snackbar/snackbar.service';

@Injectable()
export class HttpErrorHandlerInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService,
        private snackbarService: SnackbarService
    ) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                this.handleError(error, req);
                return throwError(() => error);
            })
        );
    }

    private isSilencedUrl(url: string): boolean {
        if (!url) return false;
        const profilesBase = (environment as Record<string, any>)[
            'profilesApiBaseUrl'
        ];
        return (
            (!!profilesBase && url.startsWith(profilesBase)) ||
            url.includes('clf-user-mapping') ||
            url.includes('clf-profile/basic/') ||
            url.includes('clf-profile/compliance/') ||
            url.includes('module-permissions') ||
            url.includes('createOrUpdateModelclf') ||
            url.includes('fdm-integration/mclf/send') ||
            url.includes('master/fnclyearlist') ||
            url.includes('clf-activity/main/data') ||
            url.includes('notification/sendEmail')
        );
    }

    private isSrpUrl(url: string): boolean {
        if (!url) return false;
        return url.includes('clf-profile/srp');
    }

    private isProfileUrl(url: string): boolean {
        if (!url) return false;
        return (
            url.includes('clf-profile/basic/') ||
            url.includes('clf-profile/cmtc') ||
            url.includes('clf-profile/clf-operation') ||
            url.includes('governance') ||
            url.includes('createOrUpdateModelclf')
        );
    }

    private backendMessage(error: HttpErrorResponse): string {
        const body = error?.error;
        if (body && typeof body === 'object') {
            return `${body.message || body.responseMsg || ''}`.trim();
        }
        return '';
    }

    private handleError(error: HttpErrorResponse, req?: HttpRequest<any>) {
        if (req && this.isSilencedUrl(req.url)) {
            return;
        }
        const url = req?.url || '';
        const isSrp = this.isSrpUrl(url);
        const isProfile = this.isProfileUrl(url);
        const backendMsg = this.backendMessage(error);
        switch (error.status) {
            case 0:
                this.snackbarService.errorSnackbar(
                    'Network error — please check your connection or backend availability'
                );
                break;
            case 400:
                this.handleClientErrors(error);
                break;
            case 401:
                if (isSrp) {
                    return;
                }
                if (isProfile) {
                    this.authService.logout();
                    return;
                }
                this.snackbarService.errorSnackbar(backendMsg || 'Session Expired');
                this.authService.logout();
                break;
            case 402:
            case 404:
                if (isProfile) {
                    this.authService.logout();
                    return;
                }
                if (error.status === 404) {
                    this.snackbarService.errorSnackbar(backendMsg || 'Not Found');
                }
                break;
            case 403:
                this.snackbarService.errorSnackbar(backendMsg || 'Access Denied');
                break;
            case 500:
                this.snackbarService.errorSnackbar(backendMsg || 'Internal Server Error');
                break;
            default:
                this.snackbarService.errorSnackbar(backendMsg || 'Something went wrong');
                break;
        }
    }

    private handleClientErrors(error: HttpErrorResponse) {
        const msg = error?.error?.responseMsg || error?.error?.message;
        this.snackbarService.errorSnackbar(msg || 'Bad Request');
    }
}
